import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData } from '../helpers/ApiSettings';

const RelayPoints = () => {
  const [relayPoints, setRelayPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPoint, setEditingPoint] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    postalCode: '',
    latitude: '',
    longitude: '',
    price: '',
    phone: '',
    area: ''
  });

  const fetchRelayPoints = async () => {
    try {
      setLoading(true);
      const response = await getData('/relay-points?sort[0]=name:asc');
      setRelayPoints(response.data.data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des points relais');
      console.error('Error fetching relay points:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelayPoints();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Ensure price is a number
      const dataToSubmit = {
        ...formData,
        price: parseFloat(formData.price)
      };

      if (editingPoint) {
        await putData(`/relay-points/${editingPoint.documentId}`, { data: dataToSubmit });
        alert('Point relais modifié avec succès');
      } else {
        await postData('/relay-points', { data: dataToSubmit });
        alert('Point relais créé avec succès');
      }

      // Close modal first
      const modal = document.getElementById('relayPointModal');
      const bootstrapModal = window.bootstrap.Modal.getInstance(modal);
      bootstrapModal.hide();

      // Then reset form and fetch new data
      setFormData({ name: '', address: '', city: '', postalCode: '', price: '', phone: '', area: '' });
      setEditingPoint(null);
      await fetchRelayPoints();
    } catch (err) {
      alert('Erreur lors de l\'enregistrement du point relais');
      console.error('Error saving relay point:', err);
    }
  };

  const handleEdit = (point) => {
    setEditingPoint(point);
    setFormData({
      name: point.name,
      address: point.address,
      city: point.city,
      postalCode: point.postalCode,
      latitude: point.latitude,
      longitude: point.longitude,
      price: point.price.toString(),
      phone: point.phone,
      area: point.area
    });
    // Fixed Bootstrap modal reference
    const modal = new window.bootstrap.Modal(document.getElementById('relayPointModal'));
    modal.show();
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce point relais ?')) {
      try {
        await deleteData(`/relay-points/${id}`);
        await fetchRelayPoints();
        alert('Point relais supprimé avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression du point relais');
        console.error('Error deleting relay point:', err);
      }
    }
  };

  const handleAddNew = () => {
    setEditingPoint(null);
    setFormData({ name: '', address: '', city: '', postalCode: '', price: '', area: '' });
    // Fixed Bootstrap modal reference
    const modal = new window.bootstrap.Modal(document.getElementById('relayPointModal'));
    modal.show();
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Points Relais</h2>
        <div>
          <button
            onClick={fetchRelayPoints}
            className="btn btn-outline-primary me-2"
          >
            Actualiser
          </button>
          <button
            onClick={handleAddNew}
            className="btn btn-primary"
          >
            Nouveau Point Relais
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {relayPoints.length === 0 ? (
            <p className="text-center text-muted my-5">Aucun point relais pour le moment</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Adresse</th>
                    <th>Ville</th>
                    <th>Code Postal</th>
                    <th>Prix</th>
                    <th>Téléphone</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {relayPoints.map((point) => (
                    <tr key={point.id}>
                      <td>{point.name}</td>
                      <td>{point.address}</td>
                      <td>{point.city}</td>
                      <td>{point.postalCode}</td>
                      <td>{point.price.toFixed(2)}€</td>
                      <td>{point.phone}</td>
                      <td>
                        <div className="btn-group">
                          <button
                            className="btn btn-sm btn-primary me-2"
                            onClick={() => handleEdit(point)}
                          >
                            Modifier
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(point.documentId)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Add/Edit Relay Point */}
      <div className="modal fade" id="relayPointModal" tabIndex="-1">
        <div className="modal-dialog">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingPoint ? 'Modifier le Point Relais' : 'Nouveau Point Relais'}
                </h5>
                <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">Nom</label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="address" className="form-label">Adresse</label>
                  <input
                    type="text"
                    className="form-control"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="city" className="form-label">Ville</label>
                  <input
                    type="text"
                    className="form-control"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="postalCode" className="form-label">Code Postal</label>
                  <input
                    type="text"
                    className="form-control"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="latitude" className="form-label">Latitude</label>
                      <input
                        type="text"
                        className="form-control"
                        id="latitude"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="longitude" className="form-label">Longitude</label>
                      <input
                        type="text"
                        className="form-control"
                        id="longitude"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label htmlFor="area" className="form-label">Zone Géographique</label>
                  <select
                    className="form-select"
                    name="area"
                    id="area"
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionnez une zone</option>
                    <option value="Grande-Terre" selected={formData.area === 'Grande-Terre'}>Grande-Terre</option>
                    <option value="Basse-Terre" selected={formData.area === 'Basse-Terre'}>Basse-Terre</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label htmlFor="phone" className="form-label">Téléphone</label>
                  <input
                    type="tel"
                    className="form-control"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="price" className="form-label">Prix</label>
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      id="price"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      required
                    />
                    <span className="input-group-text">€</span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPoint ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RelayPoints;